import {
  BulkUserOperationResult,
  CiPermission,
  FirstLevelPermission,
  Permission,
  EditableUserProfile,
  LoginUserProfile,
  FormValues,
  PermissionDescriptor,
  BulkPermissionsResult,
} from '../../types';
import { getEmailValidationError, MAX_USER_COUNT_TO_OPERATE, State } from '../utils';
import { API_MANAGEMENT_PERMISSION, NEXA_PERMISSION } from '@src/utils/constants';

export const validateEmails = (_, value) => {
  if (!Array.isArray(value) || value.length === 0) {
    return Promise.reject(State.emailCanNotBeEmpty);
  }
  if (value.length > MAX_USER_COUNT_TO_OPERATE) {
    return Promise.reject('Max limit reached.');
  }

  for (const it of value) {
    const error = getEmailValidationError(it);
    if (error) {
      return Promise.reject(error);
    }
  }
  return Promise.resolve();
};

export interface OperationStatusInfo {
  state: 'success' | 'fail';
  title: string;
  content: string;
}
export const FISRT_LEVEL_PERMISSION_INFO: Partial<{
  [key in FirstLevelPermission]: string;
}> = {
  canCreateActivation: 'App Activation Admin',
  canEditPrecisionPolicy: 'Precision Policy Admin',
  mdsEnabled: 'Metadata Service',
  ciEnabled: 'Audience',
};
export const FIRST_LEVEL_PERMISSION_KEYS = Object.keys(FISRT_LEVEL_PERMISSION_INFO);

/** Shown when the operator may not grant a permission (map ticket T5 / ISSUE-2). */
export const PERMISSION_NO_GRANT_TOOLTIP =
  "You don't have permission to grant this to other users.";

/**
 * The first-level permissions to render. Grantability is now enforced by the server-computed
 * `canGrant` map (T5/ISSUE-2) — the editor greys out permissions the operator can't grant — so
 * this no longer drops `canEditPrecisionPolicy` client-side based on the operator's own profile
 * (that duplicated `isInternal`-style check is removed). `profile` is kept for signature
 * compatibility but is no longer read.
 */
export function getFirstLevelPermissions(_profile?: LoginUserProfile) {
  return FISRT_LEVEL_PERMISSION_INFO;
}

export const CI_PERMISSION_INFO: {
  [key in CiPermission]: {
    label: string;
    description: string;
  };
} = {
  ciCreateSegmentPermission: {
    label: 'Segment Creation',
    description:
      'Create Adhoc segments, restricted to your package limit. The segment creator becomes the segment owner, "with permissions to edit and delete the segment.',
  },
  ciExportPermission: {
    label: 'Segment Export',
    description:
      'Export segment data in .CSV format. Set up scheduled exports for recurring data analysis.',
  },
  ciShareSegmentCreatedByMePermission: {
    label: 'Segment Sharing',
    description:
      'Share segments with individual users or all users for collaborative data sharing. By default, segments are private and cannot be accessed by others until shared. The Segment admin has the privilege to manage all segments under this account.',
  },
  ciSegmentAdminPermission: {
    label: 'Segment Admin',
    description:
      'The Segment admin has the privilege to manage all segments under this account, including editing and deleting segments.',
  },
};

export const CI_PERMISSION_KEYS = Object.keys(CI_PERMISSION_INFO) as CiPermission[];
export const PERMISSION_KEYS = [
  ...FIRST_LEVEL_PERMISSION_KEYS,
  ...CI_PERMISSION_KEYS,
] as Permission[];

/**
 * The registry-defined boolean permissions stored key-value (map tickets T1 + T2). These are
 * rendered as generic checkboxes from the server descriptor list — there is NO hardcoded entry
 * for them. They round-trip through the same `permissions` string[] the legacy booleans use.
 */
export function getKvBooleanPermissionDescriptors(
  permissionDefinitions: PermissionDescriptor[] = [],
): PermissionDescriptor[] {
  return permissionDefinitions
    .filter((def) => def.kind === 'boolean' && def.storage?.kind === 'kv')
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
}

export function getKvBooleanPermissionKeys(
  permissionDefinitions: PermissionDescriptor[] = [],
): string[] {
  return getKvBooleanPermissionDescriptors(permissionDefinitions).map((def) => def.key);
}

export function mergeWithCiPermission(permissions: Permission[], ciPermissions: CiPermission[]) {
  return [
    ...permissions.filter((it) => !CI_PERMISSION_KEYS.includes(it as CiPermission)),
    ...ciPermissions,
  ];
}
export function mergeWithNonCiPermission(permissions: Permission[], newValue: Permission[]) {
  return [
    ...newValue,
    ...permissions.filter((it) => CI_PERMISSION_KEYS.includes(it as CiPermission)),
  ];
}

export function profileInfoToFormValues(
  profile: EditableUserProfile,
  accounts: number[],
  dynamicPermissionKeys: string[] = [],
): FormValues {
  const { email, roleId, hidden, nexaPermission, apiManagementPermission } = profile;
  const allKeys = [...PERMISSION_KEYS, ...dynamicPermissionKeys] as Permission[];
  return {
    email: email ? [email] : [],
    hidden,
    accounts,
    roleId,
    permissions: allKeys.filter((key) => profile[key]),
    nexaPermission,
    apiManagementPermission,
  };
}
export function formValuesToProfileInfo(
  formValues: FormValues,
  operatorProfile: LoginUserProfile,
  dynamicPermissionKeys: string[] = [],
): {
  accounts: number[];
  emails: string[];
  profile: EditableUserProfile;
} {
  const {
    accounts = [operatorProfile.customer_id],
    email,
    hidden,
    roleId,
    permissions = [],
    nexaPermission,
    apiManagementPermission,
  } = formValues;
  const profile = { roleId };
  // No client-side grant gating here (map ticket T5 / ISSUE-2): the server enforces grant_rule
  // fail-closed in userParamsToModelProps and skips values the operator may not grant. The editor
  // already greys out ungrantable checkboxes via the server's `canGrant` map, so the removed
  // `isInternal(operatorProfile)` (hidden) and `can_edit_policies` (canEditPrecisionPolicy) checks
  // are no longer duplicated here.
  profile['hidden'] = hidden;
  roleId !== undefined && (profile['roleId'] = roleId);
  nexaPermission !== undefined && (profile['nexaPermission'] = nexaPermission);
  apiManagementPermission !== undefined &&
    (profile['apiManagementPermission'] = apiManagementPermission);
  const allKeys = [...PERMISSION_KEYS, ...dynamicPermissionKeys] as Permission[];
  for (const key of allKeys) {
    profile[key] = permissions.includes(key);
  }
  return { accounts, emails: email, profile: profile as EditableUserProfile };
}

export function groupActionResponse(result: BulkUserOperationResult): OperationStatusInfo[] {
  const results: { message: string; users: string[]; success?: boolean }[] = [];
  const { customers = [] } = result ?? {};
  for (const customer of customers) {
    const { name, users } = customer;
    for (const user of users) {
      const { key, actionStatus } = user;
      if (actionStatus?.success) {
        let target = results.find((it) => it.success);
        if (!target) {
          target = { message: 'success', users: [], success: true };
          results.push(target);
        }
        target.users.push(key + '');
        continue;
      }
      const message = `[${name}] ${actionStatus?.message ?? 'fail'}`;
      let target = results.find((it) => it.message === message);
      if (!target) {
        target = {
          message,
          users: [],
        };
        results.push(target);
      }
      target.users.push(key + '');
    }
  }

  return results.map((it) => {
    const { message, users, success } = it;
    return {
      title: users.join(', '),
      content: message,
      state: success ? 'success' : 'fail',
    };
  });
}
/**
 * Convert the bulk permission-edit result (map ticket T6 / ISSUE-5) into the same OperationStatusInfo
 * shape the editor's result panel renders (updated / skipped-with-reason / failed-with-reason). One
 * success line lists every updated user; skipped/failed users are grouped by their reason so the
 * report reads "these N users were skipped because X".
 */
export function bulkResultToStatusInfo(result: BulkPermissionsResult): OperationStatusInfo[] {
  const out: OperationStatusInfo[] = [];
  if (result.updated > 0) {
    out.push({
      state: 'success',
      title: `${result.updated} user${result.updated === 1 ? '' : 's'} updated`,
      content: 'Permissions applied',
    });
  }
  const byReason = (
    rows: BulkPermissionsResult['skipped'],
    kind: 'skipped' | 'failed',
  ): OperationStatusInfo[] => {
    const groups = new Map<string, string[]>();
    for (const row of rows) {
      const reason = row.reason ?? kind;
      const label = row.email ?? String(row.id);
      const list = groups.get(reason) ?? [];
      list.push(label);
      groups.set(reason, list);
    }
    return Array.from(groups.entries()).map(([reason, users]) => ({
      state: 'fail',
      title: users.join(', '),
      content: `${kind === 'skipped' ? 'Skipped' : 'Failed'}: ${reason}`,
    }));
  };
  out.push(...byReason(result.skipped, 'skipped'));
  out.push(...byReason(result.failed, 'failed'));
  return out;
}

export const USER_ROLE_ADMIN = 1;
export const USER_ROLE_STAFF = 2;
export const USER_ROLE_READ_ONLY = 14;

export const defaultProfileForEditor: EditableUserProfile = {
  email: '',
  roleId: USER_ROLE_STAFF,
  hidden: true,
  canCreateActivation: false,
  canEditPrecisionPolicy: false,
  mdsEnabled: false,
  ciEnabled: false,
  ciCreateSegmentPermission: false,
  ciExportPermission: false,
  ciSegmentAdminPermission: false,
  ciShareSegmentCreatedByMePermission: false,
  nexaPermission: NEXA_PERMISSION.USAGE,
  // Default role is Staff -> Edit. The editor keeps this in sync with the
  // selected role on invite; the backend applies the same role-based default.
  apiManagementPermission: API_MANAGEMENT_PERMISSION.EDIT,
};
